/**
 * FAQ Page Component
 * Client-facing explanations about the booking system
 */

import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import './FAQ.css';

const faqItems = [
  {
    category: 'Buchung',
    questions: [
      {
        q: 'Wie buche ich eine Trainingseinheit?',
        a: 'Gehe zum Trainingskalender, wähle den gewünschten Tag und tippe bei der gewünschten Uhrzeit auf „1 Person“ oder „2 Personen“. Nach einer kurzen Rückfrage ist die Buchung bestätigt.'
      },
      {
        q: 'Was bedeuten die verschiedenen Farben im Kalender?',
        a: 'Grün markierte Plätze sind deine eigenen Buchungen. Blau markierte Plätze sind von anderen Mitgliedern belegt. Grau gestrichelte Plätze sind frei und buchbar. Slots mit gelbem Rand und "Peak"-Label sind Stoßzeiten.'
      },
      {
        q: 'Was sind Stoßzeiten (Peak)?',
        a: 'Stoßzeiten sind besonders beliebte Trainingszeiten, die im Kalender gelb markiert und mit einem "Peak"-Label versehen sind. Slots sind grundsätzlich immer von 0–24 Uhr verfügbar — Stoßzeiten markieren nur die besonders gefragten Zeiträume. Für Stoßzeiten kann ein separates Trainingszeitlimit gelten.'
      },
      {
        q: 'Was bedeutet „1 Person“ und „2 Personen“?',
        a: 'Bei „1 Person“ trainierst du allein, andere Mitglieder können den freien Platz noch buchen. Bei „2 Personen“ belegst du zwei Plätze — für dich und eine Begleitung oder wenn du das Studio für dich allein haben möchtest. Die Trainingszeit wird in beiden Fällen nur einmal gezählt.'
      },
      {
        q: 'Wie weit im Voraus kann ich buchen?',
        a: 'Die maximale Vorausbuchungszeit wird vom Studio festgelegt. Du siehst im Kalender nur Tage, die innerhalb dieses Zeitraums liegen.'
      },
      {
        q: 'Was bedeutet "Ausgebucht"?',
        a: 'Wenn alle verfügbaren Plätze zu einem Zeitpunkt belegt sind, wird der Slot als "Ausgebucht" angezeigt. Du kannst dann keinen weiteren Platz buchen.'
      }
    ]
  },
  {
    category: 'Trainingszeit',
    questions: [
      {
        q: 'Wie wird meine Trainingszeit berechnet?',
        a: 'Deine Trainingszeit pro Tag ergibt sich aus der Anzahl der verschiedenen gebuchten Uhrzeiten multipliziert mit der Slot-Dauer. Wenn du z.B. bei 30-Minuten-Slots zwei verschiedene Uhrzeiten buchst, sind das 60 Minuten Trainingszeit — unabhängig davon, für wie viele Personen du gebucht hast.'
      },
      {
        q: 'Was ist das tägliche Trainingszeitlimit?',
        a: 'Das Studio kann eine maximale Trainingszeit pro Tag festlegen. Wenn dieses Limit erreicht ist, kannst du für diesen Tag keine weiteren Uhrzeiten buchen. Das Limit wird im Kalender-Header angezeigt.'
      },
      {
        q: 'Was ist das Stoßzeiten-Trainingslimit?',
        a: 'Zusätzlich zum täglichen Limit kann es ein separates Limit für Stoßzeiten geben. Die Trainingszeit in Stoßzeiten zählt sowohl zum Stoßzeiten-Limit als auch zum täglichen Gesamtlimit.'
      }
    ]
  },
  {
    category: 'Stornierung',
    questions: [
      {
        q: 'Wie storniere ich eine Buchung?',
        a: 'Direkt im Trainingskalender über „Stornieren“ bei deiner Buchung oder unter „Meine Buchungen“ in der Seitenleiste. Storniert wird immer die ganze Buchung, also auch die Begleitung.'
      },
      {
        q: 'Gibt es eine Stornierungsfrist?',
        a: 'Ja, Stornierungen müssen innerhalb der festgelegten Vorlaufzeit erfolgen. Die genaue Frist wird vom Studio bestimmt. Bei zu später Stornierung wird die Buchung als No-Show gewertet.'
      },
      {
        q: 'Was passiert bei einem No-Show?',
        a: 'Wenn du nicht zum Training erscheinst und nicht rechtzeitig storniert hast, wird dies als No-Show gewertet. Aus Rücksicht auf die anderen Mitglieder bitten wir dich, deine Einheit immer rechtzeitig zu stornieren.'
      }
    ]
  },
  {
    category: 'Konto & Allgemein',
    questions: [
      {
        q: 'Wo sehe ich meine gebuchten Trainingseinheiten?',
        a: 'Unter "Meine Buchungen" in der Seitenleiste findest du eine Übersicht aller deiner vergangenen und zukünftigen Buchungen.'
      },
      {
        q: 'Kann ich mein Passwort ändern?',
        a: 'Ja, klicke auf der Login-Seite auf "Passwort vergessen". Du erhältst dann eine E-Mail mit einem Link, über den du ein neues Passwort setzen kannst. Der Link ist 30 Minuten gültig.'
      }
    ]
  }
];

const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className={`faq-item ${isOpen ? 'open' : ''}`}>
      <button className="faq-question" onClick={() => setIsOpen(!isOpen)}>
        <span>{question}</span>
        <svg
          className={`faq-chevron ${isOpen ? 'rotated' : ''}`}
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {isOpen && (
        <div className="faq-answer">
          <p>{answer}</p>
        </div>
      )}
    </div>
  );
};

const FAQ = () => {
  const { user } = useAuth();

  return (
    <div className="faq-page">
      <div className="faq-header">
        <h1>Häufig gestellte Fragen{user?.studioName ? ` – ${user.studioName}` : ''}</h1>
        <p>Hier findest du Antworten auf die wichtigsten Fragen rund um den Trainingskalender und das Buchungssystem.</p>
      </div>

      {faqItems.map((section) => (
        <div key={section.category} className="faq-section">
          <h2>{section.category}</h2>
          <div className="faq-list">
            {section.questions.map((item, idx) => (
              <FAQItem key={idx} question={item.q} answer={item.a} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default FAQ;
